import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import { Popper, Typography } from '@material-ui/core'
import { ClassNameMap } from '@material-ui/core/styles/withStyles'
import { useShallowEditorActionBar } from 'components/Workspace2EditorActionBar/store/EditorActionBarStore'
import {
    ConfigItem,
    VisibilitySettingsActionType,
    VisibilitySettingsStates,
} from 'components/WorkspaceEditor/types/editorActionBar.type'
import React, { memo, useCallback, useEffect, useMemo } from 'react'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { VisibilitySettingsMenuItem } from './VisibilitySettingsMenuItem'

interface VisibilitySettingsPopoverProps {
    isOpen: boolean
    classes: ClassNameMap<string>
    elementRef: React.RefObject<HTMLDivElement>
}
//generate menu items for visilibiity settings popover component
// create pure configs w/o component
const VISIBILITY_MENU_ITEMS_CONFIG = [
    {
        key: 'areNotesVisible',
        actionLabel: VisibilitySettingsActionType.NotesToSpecifier,
        testId: 'visibility-noteToSpecifier',
    },
    {
        key: 'isInactiveContentVisible',
        actionLabel: VisibilitySettingsActionType.InactiveContent,
        testId: 'visibility-inactiveContent',
    },
    {
        key: 'areTrackChangesVisible',
        actionLabel: VisibilitySettingsActionType.TrackChanges,
        testId: 'visibility-trackChanges',
    },
    {
        key: 'arePTLinksVisible',
        actionLabel: VisibilitySettingsActionType.PTLink,
        testId: 'visibility-ptlink',
    },
] as const

export const VisibilitySettingsPopover = memo<VisibilitySettingsPopoverProps>(
    ({ classes, elementRef, isOpen }) => {
        const containerRef = React.useRef<HTMLDivElement>(null)

        const {
            actions: { setIsVisibilitySettingsMenuDisplayed },
        } = useShallowEditorActionBar((state) => ({
            actions: state.actions,
        }))

        const { previewFeatures } = usePreviewFeatures()
        const renderVisibilitySettingsItem = useCallback(
            (item: ConfigItem, classes: ClassNameMap<string>): MenuItemConfig => ({
                dataTestId: item.testId,
                component: (
                    <VisibilitySettingsMenuItem
                        key={item.key}
                        selectedKey={item.key as keyof VisibilitySettingsStates}
                        actionLabel={
                            item.actionLabel as VisibilitySettingsActionType
                        }
                        classes={classes}
                    />
                ),
            }),
            [classes]
        )
        const menuItems: MenuItemConfig[] = useMemo(
            () =>
                VISIBILITY_MENU_ITEMS_CONFIG.filter((item) => {
                    if (item.key === 'arePTLinksVisible')
                        return previewFeatures.hasOMPTLinkAutomation
                    return true
                }).map((item) => renderVisibilitySettingsItem(item, classes)),
            [classes, previewFeatures.hasOMPTLinkAutomation, renderVisibilitySettingsItem]
        )

        /**
         * Adds an event listener to detect clicks outside the container.
         * If a click is detected outside, it will close the Settings menu.
         */
        useEffect(() => {
            const handleMouseDown = (event: MouseEvent) => {
                if (
                    containerRef.current &&
                    !containerRef.current.contains(event.target as Node)
                ) {
                    setIsVisibilitySettingsMenuDisplayed(false)
                }
            }

            document.addEventListener('mousedown', handleMouseDown)
            return () => {
                document.removeEventListener('mousedown', handleMouseDown)
            }
        }, [containerRef, setIsVisibilitySettingsMenuDisplayed])

        return (
            <div
                ref={containerRef}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <Popper
                    open={isOpen}
                    anchorEl={elementRef.current}
                    className={classes.visibiltySettingWrapper}
                    placement="bottom-start"
                    data-testid="visibilitySettings-popover"
                >
                    <Typography className={classes.viewText}>View:</Typography>
                    {menuItems.map(({ component }) => component)}
                </Popper>
            </div>
        )
    }
)
